import type {
  BadgePrintJob,
  BootstrapResponse,
  BootstrapStatusResponse,
  ConciergeDelivery,
  ConciergeCommunicationSession,
  ConciergePerson,
  ConciergeSession,
  ConciergeSetting,
  ContractorVisit,
  GuestVisit,
  PlayerCommand,
  PlayerCommandAckStatus,
  TerminalConfiguration
} from "./types";
import { startupError, startupLog } from "./startupDiagnostics";

type RequestOptions = {
  method?: string;
  token?: string | null;
  body?: unknown;
  retries?: number;
  retryLabel?: string;
};

function delay(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isRetryableError(error: unknown) {
  const status = (error as Error & { status?: number })?.status;
  if (!status) return true;
  return [408, 425, 429, 500, 502, 503, 504].includes(status);
}

function retryDelay(attempt: number) {
  const base = Math.min(15_000, 750 * (2 ** attempt));
  return base + Math.floor(Math.random() * 350);
}

async function requestOnce<T>(path: string, { method = "GET", token, body }: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: body ? JSON.stringify(body) : undefined
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = payload?.error?.message || "Something went wrong.";
    const error = new Error(message);
    (error as Error & { status?: number; payload?: unknown }).status = response.status;
    (error as Error & { status?: number; payload?: unknown }).payload = payload;
    throw error;
  }

  return payload as T;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const retries = options.retries || 0;
  const label = options.retryLabel || path;

  startupLog("API_REQUEST_START", { label, path, method: options.method || "GET", retries });

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const payload = await requestOnce<T>(path, options);
      startupLog("API_REQUEST_COMPLETE", { label, attempt });
      return payload;
    } catch (error) {
      const finalAttempt = attempt >= retries || !isRetryableError(error);

      if (finalAttempt) {
        startupError("API_REQUEST_FAILED", error, { label, path, attempt });
        throw error;
      }

      const waitMs = retryDelay(attempt);
      startupLog("API_REQUEST_RETRY", {
        label,
        path,
        attempt: attempt + 1,
        wait_ms: waitMs,
        online: navigator.onLine
      });
      await delay(waitMs);
    }
  }

  throw new Error("Request retry loop exited unexpectedly.");
}

export const terminalApi = {
  bootstrap(payload: { installation_uuid: string; platform: string; app_version: string; metadata: Record<string, unknown>; capabilities?: Record<string, unknown> }) {
    return request<BootstrapResponse>("/api/players/bootstrap", { method: "POST", body: payload, retries: 5, retryLabel: "PLAYER_BOOTSTRAP" });
  },

  renewActivation(pollToken: string) {
    return request<BootstrapStatusResponse>("/api/players/bootstrap/renew", { method: "POST", token: pollToken });
  },

  bootstrapStatus(pollToken: string) {
    return request<BootstrapStatusResponse>("/api/players/bootstrap/status", { token: pollToken, retries: 0, retryLabel: "PLAYER_BOOTSTRAP_STATUS" });
  },

  configuration(deviceToken: string) {
    return request<TerminalConfiguration>("/api/players/config", { token: deviceToken, retries: 5, retryLabel: "PLAYER_CONFIGURATION" });
  },

  saveConfiguration(deviceToken: string, payload: Record<string, unknown>) {
    return request<TerminalConfiguration>("/api/players/config", { method: "POST", token: deviceToken, body: payload });
  },

  heartbeat(deviceToken: string, payload: Record<string, unknown> = {}) {
    return request<TerminalConfiguration>("/api/players/heartbeat", { method: "POST", token: deviceToken, body: payload });
  },

  commands(deviceToken: string) {
    return request<{ commands: PlayerCommand[] }>("/api/players/commands", { token: deviceToken });
  },

  acknowledgeCommand(deviceToken: string, commandId: string, payload: { status: PlayerCommandAckStatus; error_message?: string }) {
    return request<{ ok: true; command: PlayerCommand }>(`/api/players/commands/${commandId}/ack`, { method: "POST", token: deviceToken, body: payload });
  },

  realtimeStatus(deviceToken: string, payload: { status: "connected" | "disconnected" | "error"; error_message?: string; config_version_applied?: number }) {
    return request<{ ok: true; realtime_status: string; config_version_applied: number }>("/api/players/realtime/status", { method: "POST", token: deviceToken, body: payload });
  },

  conciergeConfig(deviceToken: string) {
    return request<{ setting: ConciergeSetting }>("/api/players/concierge/config", { token: deviceToken });
  },

  searchPeople(deviceToken: string, query: string) {
    return request<{ people: ConciergePerson[] }>(`/api/players/concierge/people?q=${encodeURIComponent(query)}`, { token: deviceToken });
  },

  createConciergeSession(deviceToken: string, flowType: string) {
    return request<{ session: ConciergeSession }>("/api/players/concierge/sessions", {
      method: "POST",
      token: deviceToken,
      body: { flow_type: flowType }
    });
  },

  cancelConciergeSession(deviceToken: string, sessionId: string) {
    return request<{ session: ConciergeSession }>(`/api/players/concierge/sessions/${sessionId}/cancel`, { method: "POST", token: deviceToken });
  },

  createGuestVisit(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ guest_visit: GuestVisit }>("/api/players/concierge/guest_visits", { method: "POST", token: deviceToken, body: payload });
  },

  uploadGuestPhoto(deviceToken: string, guestVisitId: string, payload: { image_data: string; filename?: string }) {
    return request<{ guest_visit: GuestVisit }>(`/api/players/concierge/guest_visits/${guestVisitId}/photo`, { method: "POST", token: deviceToken, body: payload });
  },

  notifyGuestHost(deviceToken: string, guestVisitId: string) {
    return request<{ guest_visit: GuestVisit; notification_event_id: string }>(`/api/players/concierge/guest_visits/${guestVisitId}/notify`, { method: "POST", token: deviceToken });
  },

  queueGuestBadge(deviceToken: string, guestVisitId: string) {
    return request<{ badge_print_job: BadgePrintJob }>(`/api/players/concierge/guest_visits/${guestVisitId}/badge`, { method: "POST", token: deviceToken });
  },

  completeGuestVisit(deviceToken: string, guestVisitId: string) {
    return request<{ guest_visit: GuestVisit }>(`/api/players/concierge/guest_visits/${guestVisitId}/complete`, { method: "POST", token: deviceToken });
  },

  createDelivery(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ delivery: ConciergeDelivery }>("/api/players/concierge/deliveries", { method: "POST", token: deviceToken, body: payload });
  },

  notifyDeliveryRecipient(deviceToken: string, deliveryId: string) {
    return request<{ delivery: ConciergeDelivery; notification_event_id: string }>(`/api/players/concierge/deliveries/${deliveryId}/notify`, { method: "POST", token: deviceToken });
  },

  completeDelivery(deviceToken: string, deliveryId: string) {
    return request<{ delivery: ConciergeDelivery }>(`/api/players/concierge/deliveries/${deliveryId}/complete`, { method: "POST", token: deviceToken });
  },

  createMemberCheckIn(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ member_check_in: Record<string, unknown> }>("/api/players/concierge/member_check_ins", { method: "POST", token: deviceToken, body: payload });
  },

  createCommunication(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ communication_session: ConciergeCommunicationSession; invite_url: string; provider: Record<string, unknown> }>("/api/players/concierge/communications", {
      method: "POST",
      token: deviceToken,
      body: payload
    });
  },

  communicationToken(deviceToken: string, communicationSessionId: string) {
    return request<{ communication_session: ConciergeCommunicationSession; provider: Record<string, unknown> }>(`/api/players/concierge/communications/${communicationSessionId}/token`, {
      method: "POST",
      token: deviceToken
    });
  },

  communication(deviceToken: string, communicationSessionId: string) {
    return request<{ communication_session: ConciergeCommunicationSession }>(`/api/players/concierge/communications/${communicationSessionId}`, {
      token: deviceToken
    });
  },

  endCommunication(deviceToken: string, communicationSessionId: string) {
    return request<{ communication_session: ConciergeCommunicationSession }>(`/api/players/concierge/communications/${communicationSessionId}/end`, {
      method: "POST",
      token: deviceToken
    });
  },

  createContractorVisit(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ contractor_visit: ContractorVisit }>("/api/players/concierge/contractor_visits", {
      method: "POST",
      token: deviceToken,
      body: payload
    });
  },

  completeContractorVisit(deviceToken: string, contractorVisitId: string) {
    return request<{ contractor_visit: ContractorVisit }>(`/api/players/concierge/contractor_visits/${contractorVisitId}/complete`, {
      method: "POST",
      token: deviceToken
    });
  },

  departContractorVisit(deviceToken: string, contractorVisitId: string) {
    return request<{ contractor_visit: ContractorVisit }>(`/api/players/concierge/contractor_visits/${contractorVisitId}/depart`, {
      method: "POST",
      token: deviceToken
    });
  },

  syncOfflineEvent(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ offline_event: Record<string, unknown>; duplicate: boolean }>("/api/players/concierge/offline_events", {
      method: "POST",
      token: deviceToken,
      body: payload
    });
  },

  trackAnalyticsEvent(deviceToken: string, payload: Record<string, unknown>) {
    return request<{ analytics_event: Record<string, unknown> }>("/api/players/concierge/analytics_events", {
      method: "POST",
      token: deviceToken,
      body: payload
    });
  }
};
